import config from '../config/env';
import React, { createContext, useContext, useState, useEffect, ReactNode, useMemo, useCallback } from 'react';
import enTranslations from '../i18n/en-GB.json';
import { safeStorage } from '../utils/safeStorage';

// Supported Languages. The codes are BCP 47 tags so they can be passed
// directly to Intl.DateTimeFormat / toLocaleString — ``en-GB`` yields
// DD/MM/YYYY out of the box, matching the French format we use in the
// rest of the UI.
export type SupportedLanguage = 'en-GB' | 'fr';

export const DEFAULT_LANGUAGE: SupportedLanguage = 'en-GB';

export interface LanguageInfo {
  code: SupportedLanguage;
  name: string;
  nativeName: string;
  flag: string;
}

export const SUPPORTED_LANGUAGES: Record<SupportedLanguage, LanguageInfo> = {
  'en-GB': {
    code: 'en-GB',
    name: 'English',
    nativeName: 'English',
    flag: '🇬🇧'
  },
  fr: {
    code: 'fr',
    name: 'French',
    nativeName: 'Français',
    flag: '🇫🇷'
  }
};

// Translation key shape is inferred from the en-GB JSON; no manual mirror.
export type TranslationKeys = typeof enTranslations;

// Recursive dot-path: every leaf path of the translation tree as a literal
// string union. Lets t() reject typos at compile time and lets IDEs auto-
// complete keys. Bounded by the JSON's natural depth (~5 levels).
export type DotPath<T> = T extends string
  ? never
  : T extends readonly unknown[]
    ? never
    : T extends object
      ? {
          [K in keyof T & string]: T[K] extends string
            ? K
            : `${K}.${DotPath<T[K]>}`;
        }[keyof T & string]
      : never;

export type TranslationKey = DotPath<TranslationKeys>;

const defaultTranslations: TranslationKeys = enTranslations;

// I18n Context
interface I18nContextType {
  language: SupportedLanguage;
  translations: TranslationKeys;
  availableLanguages: LanguageInfo[];
  setLanguage: (language: SupportedLanguage) => void;
  t: (key: TranslationKey, params?: Record<string, string | number>) => string;
  formatDate: (date: Date | string | number, options?: Intl.DateTimeFormatOptions) => string;
  formatDateTime: (date: Date | string | number, options?: Intl.DateTimeFormatOptions) => string;
  formatTime: (date: Date | string | number, options?: Intl.DateTimeFormatOptions) => string;
  formatNumber: (number: number, options?: Intl.NumberFormatOptions) => string;
  formatCurrency: (amount: number, currency?: string) => string;
  formatRelativeTime: (date: Date | string | number) => string;
  isRTL: boolean;
}

const I18nContext = createContext<I18nContextType | undefined>(undefined);

// Map any browser / legacy-storage language tag to one of our
// supported codes. Handles the old ``'en'`` value that used to be
// stored in localStorage before we moved to the BCP 47 tag ``'en-GB'``,
// and maps ``'en-US'`` / ``'en-AU'`` / etc. to en-GB so anglophone
// users don't land on the French translations.
const normaliseLanguageTag = (tag: string | null | undefined): SupportedLanguage | null => {
  if (!tag) return null;
  const lower = tag.toLowerCase();
  if (lower === 'fr' || lower.startsWith('fr-')) return 'fr';
  if (lower === 'en' || lower.startsWith('en')) return 'en-GB';
  return null;
};

// I18n Provider
export const I18nProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [language, setLanguageState] = useState<SupportedLanguage>(DEFAULT_LANGUAGE);
  const [translations, setTranslations] = useState<TranslationKeys>(defaultTranslations);

  // Load translations. Toggling languages quickly (en-GB ↔ fr ↔ en-GB)
  // could otherwise let an earlier in-flight import resolve last and
  // leave the wrong language displayed. Each effect run owns a `cancelled`
  // flag that the cleanup flips before the next run starts.
  useEffect(() => {
    let cancelled = false;
    const loadTranslations = async () => {
      try {
        if (language === 'en-GB') {
          if (!cancelled) setTranslations(defaultTranslations);
          return;
        }

        const { default: frTranslations } = await import('../i18n/fr.json');
        if (cancelled) return;
        setTranslations(frTranslations);

        if (config.dev.debug) {
          console.log('[I18N DEBUG] Loaded translations for:', language);
        }
      } catch (error) {
        if (cancelled) return;
        console.error('[I18N] Failed to load translations for:', language, error);
        setTranslations(defaultTranslations);
      }
    };

    loadTranslations();
    return () => {
      cancelled = true;
    };
  }, [language]);

  // Initialize language strictly from explicit user choice (localStorage).
  // Legacy ``'en'`` values stored before the switch to BCP 47 tags are
  // transparently upgraded to ``'en-GB'`` via normaliseLanguageTag.
  //
  // We deliberately do NOT auto-detect from ``navigator.language``: the
  // app default is ``DEFAULT_LANGUAGE`` (en-GB), and users opt into
  // French via the language selector. This avoids the surprise where a
  // first-time visitor on a French-locale browser lands on a French UI
  // despite en-GB being the documented default.
  useEffect(() => {
    const stored = normaliseLanguageTag(safeStorage.getItem('preferred-language'));
    if (stored) {
      setLanguageState(stored);
      // Rewrite the stored tag so legacy ``'en'`` values get upgraded.
      safeStorage.setItem('preferred-language', stored);
    }
  }, []);

  // Fetch server-side default language and apply when the user has not
  // picked one locally (``preferred-language`` key absent). Applies
  // in-memory only — not persisted to localStorage — so SuperAdmin changes
  // keep propagating on every reload until the user opts in via the
  // language selector.
  useEffect(() => {
    let cancelled = false;
    const applyServerDefault = async () => {
      if (cancelled) return;
      try {
        if (safeStorage.getItem('preferred-language')) return;
      } catch { /* private mode — treat as not customised */ }
      try {
        const { fetchEffectiveOnce } = await import('../services/uiSettingsService');
        const eff = await fetchEffectiveOnce();
        if (cancelled || !eff?.settings?.language) return;
        const normalized = normaliseLanguageTag(eff.settings.language);
        if (normalized) setLanguageState(normalized);
      } catch { /* silent — built-in default stays in effect */ }
    };
    applyServerDefault();
    const listener = () => applyServerDefault();
    window.addEventListener('app:auth-ready', listener);
    return () => { cancelled = true; window.removeEventListener('app:auth-ready', listener); };
  }, []);

  const setLanguage = useCallback((newLanguage: SupportedLanguage) => {
    setLanguageState(newLanguage);
    safeStorage.setItem('preferred-language', newLanguage);

    if (config.dev.debug) {
      console.log('[I18N DEBUG] Language changed to:', newLanguage);
    }
  }, []);

  // Translation function with nested key support
  const t = useCallback((key: TranslationKey, params?: Record<string, string | number>): string => {
    try {
      const keys = key.split('.');
      let value: any = translations;

      for (const k of keys) {
        value = value?.[k];
        if (value === undefined) break;
      }

      if (typeof value !== 'string') {
        console.warn(`[I18N] Translation key not found: ${key}`);
        return key;
      }

      // Parameter substitution
      if (params) {
        return value.replace(/\{\{(\w+)\}\}/g, (match: string, paramKey: string) => {
          return params[paramKey]?.toString() || match;
        });
      }

      return value;
    } catch (error) {
      console.error('[I18N] Translation error for key:', key, error);
      return key;
    }
  }, [translations]);

  // Date-only formatting (locale-aware, no time component)
  const formatDate = useCallback((
    date: Date | string | number,
    options: Intl.DateTimeFormatOptions = {}
  ): string => {
    try {
      const dateObj = new Date(date);
      return dateObj.toLocaleDateString(language, options);
    } catch (error) {
      return String(date);
    }
  }, [language]);

  // Full date + time formatting (locale-aware)
  const formatDateTime = useCallback((
    date: Date | string | number,
    options: Intl.DateTimeFormatOptions = {}
  ): string => {
    try {
      const dateObj = new Date(date);
      return dateObj.toLocaleString(language, options);
    } catch (error) {
      return String(date);
    }
  }, [language]);

  // Time-only formatting (locale-aware)
  const formatTime = useCallback((
    date: Date | string | number,
    options: Intl.DateTimeFormatOptions = {}
  ): string => {
    try {
      const dateObj = new Date(date);
      return dateObj.toLocaleTimeString(language, options);
    } catch (error) {
      return String(date);
    }
  }, [language]);

  // Number formatting
  const formatNumber = useCallback((
    number: number,
    options: Intl.NumberFormatOptions = {}
  ): string => {
    try {
      return number.toLocaleString(language, options);
    } catch (error) {
      return String(number);
    }
  }, [language]);

  // Currency formatting
  const formatCurrency = useCallback((amount: number, currency: string = 'USD'): string => {
    try {
      return amount.toLocaleString(language, {
        style: 'currency',
        currency
      });
    } catch (error) {
      return `${currency} ${amount}`;
    }
  }, [language]);

  // Relative time formatting. Renders the LARGEST applicable unit with
  // correct singular/plural agreement so "1 hour ago" instead of the
  // previous "1 hours ago" rendering, and "just now" for sub-minute.
  // Falls through to absolute date once older than a week.
  const formatRelativeTime = useCallback((date: Date | string | number): string => {
    try {
      const dateObj = new Date(date);
      const now = new Date();
      const diffMs = now.getTime() - dateObj.getTime();
      const diffSeconds = Math.floor(diffMs / 1000);
      const diffMinutes = Math.floor(diffSeconds / 60);
      const diffHours = Math.floor(diffMinutes / 60);
      const diffDays = Math.floor(diffHours / 24);

      const unit = (count: number, singularKey: string, pluralKey: string) =>
        count === 1 ? t(singularKey as Parameters<typeof t>[0]) : t(pluralKey as Parameters<typeof t>[0]);

      if (diffSeconds < 60) return t('time.now');
      if (diffMinutes < 60) return `${diffMinutes} ${unit(diffMinutes, 'time.minute', 'time.minutes')} ${t('time.ago')}`;
      if (diffHours < 24) return `${diffHours} ${unit(diffHours, 'time.hour', 'time.hours')} ${t('time.ago')}`;
      if (diffDays < 7) return `${diffDays} ${unit(diffDays, 'time.day', 'time.days')} ${t('time.ago')}`;

      return formatDate(dateObj);
    } catch (error) {
      return String(date);
    }
  }, [t, formatDate]);

  const contextValue: I18nContextType = useMemo(() => ({
    language,
    translations,
    availableLanguages: Object.values(SUPPORTED_LANGUAGES),
    setLanguage,
    t,
    formatDate,
    formatDateTime,
    formatTime,
    formatNumber,
    formatCurrency,
    formatRelativeTime,
    isRTL: false // None of our supported languages are RTL
  }), [language, translations, setLanguage, t, formatDate, formatDateTime, formatTime, formatNumber, formatCurrency, formatRelativeTime]);

  return (
    <I18nContext.Provider value={contextValue}>
      {children}
    </I18nContext.Provider>
  );
};

// Hook
export const useI18n = (): I18nContextType => {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error('useI18n must be used within an I18nProvider');
  }
  return context;
};

